import React from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'underscore';
import { FormSection } from 'AppSrc/core/components/forms/form-section';
import {
  modalDisplayDataShape,
  formDataShape,
} from 'AppSrc/core/components/forms/form-base-components/data-structure/v-f-proptypes';
// import { userRolesPropShape } from 'AppSrc/core/auth/auth-proptypes';
// Key values that are repeated a lot
import { defaultFieldLogic } from 'AppSrc/core/components/forms/form-base-components/data-structure/default-field-logic';
import { modalOptionsShape } from 'AppSrc/core/components/forms/modal-options';
import { RT_INCIDENT } from 'AppSrc/core/components/forms/value-dependency-constants';
import { RT_CI_UNAVAILABILITY } from 'AppSrc/core/components/forms/form-types/create-crq/relationships/relationships-modal/relationship-constants';
import { getDisplayOptionsToUse } from 'AppSrc/core/components/forms/data/get-display-options-to-use';
import { relationshipType } from './relationship-type';
import { relationshipHowRelated } from './relationship-how-related';
import { getIncidentFlow } from './incident-flow/get-incident-flow';
import { getConfigItemFlow } from './config-item-flow/get-config-item-flow';
import { getProjectFlow } from './project-flow/get-project-flow';
import './relationships.css';

// Title for the modal
const sectionTitleAdd = 'Add relationship';
const sectionTitleEdit = 'Edit relationship';
// All of the available fields, this is needed so that the FormSection can instantiate with the currect initial values



// callback for the form section to figure out what fields to render with conditional logic
const getCurrentFields = (params) => {
  const {
    values = {},
    formData = {},
    touched = {},
    userRoles,
    sectionId,
    // the form that exists outside of the modal form
    outerFormData,
  } = params;

  const noValues = isEmpty(values);
  const relationshipTypeValue = noValues ? null : values[relationshipType.id];

  const isCiUnavailability = relationshipTypeValue === RT_CI_UNAVAILABILITY;
  const showTypeField = !isCiUnavailability;

  const currentFields = [];


  currentFields.push({
    ...defaultFieldLogic,
    componentData: relationshipType,
    shouldRender: showTypeField,
  });

  // howRelated dropdown should only show if we have selected a relationship type
  // or if the type is unavailability
  const hasTypeValue = Boolean(relationshipTypeValue);
  const showHowRelated = hasTypeValue && !isCiUnavailability;
  // we should not overwrite relationship type for CIUnavailability
  const overwriteHowRelated = !hasTypeValue && !isCiUnavailability;
  // display options for howRelated depend on the relationship type
  // CI and project use the default set, Incident has a specific set
  // the keys for this are relationship_how_related and relationship_how_related-incident
  const isIncident = relationshipTypeValue === RT_INCIDENT;
  const displayOptionKey = `${relationshipHowRelated.id}${isIncident ? '-incident' : ''}`;
  currentFields.push({
    ...defaultFieldLogic,
    componentData: {
      ...relationshipHowRelated,
      displayOptions: getDisplayOptionsToUse({ generatedKey: displayOptionKey }),
    },
    shouldRender: showHowRelated,
    shouldValidate: showHowRelated,
    overwriteSubmit: {
      overwrite: overwriteHowRelated,
      overwriteValue: null,
    },
  });

  const incidentFields = getIncidentFlow({
    relationshipTypeValue,
    values,
    formData,
    touched,
    userRoles,
    sectionId,
    // the form that exists outside of the modal form
    outerFormData,
  });

  const projectFields = getProjectFlow({
    relationshipTypeValue,
    values,
    formData,
    touched,
    userRoles,
    sectionId,
    // the form that exists outside of the modal form
    outerFormData,
  });

  const configItemFields = getConfigItemFlow({
    relationshipTypeValue,
    values,
    formData,
    touched,
    userRoles,
    sectionId,
    // the form that exists outside of the modal form
    outerFormData,
  });

  // FN:28/05/2020
  // As this modal has to be able to create all three types of relationships the initial values for all types of modals have
  // to be set to null.
  // To simplify the overal logic of the modal we extract out the three different different flows created by the first option
  // and add them here.
  currentFields.push(...incidentFields);
  currentFields.push(...projectFields);
  currentFields.push(...configItemFields);

  return currentFields;
};


// generates the formSection!
const RelationshipsModal = React.memo(({
  handleSuccessfulSubmit,
  // userRoles, // <- currently not passed in but may be needed to one day (maybe through redux?)
  formData,
  outerFormData,
  modalOptions,
  modalDisplayData,
  editingStatus,
}) => {
  const sectionTitle = modalDisplayData.type === 'edit' ? sectionTitleEdit : sectionTitleAdd;
  return (
    <FormSection
      sectionTitle={sectionTitle}
      sectionId={modalDisplayData.sectionId}
      getCurrentFields={getCurrentFields}
      // userRoles={userRoles} <- I don't think this is currently passed in.
      formData={formData}
      outerFormData={outerFormData}
      modalOptions={modalOptions}
      handleSuccessfulSubmit={handleSuccessfulSubmit}
      editingStatus={editingStatus}
    />
  );
});


RelationshipsModal.propTypes = {
  handleSuccessfulSubmit: PropTypes.func,
  modalDisplayData: modalDisplayDataShape,
  // userRoles: userRolesPropShape,
  formData: formDataShape,
  outerFormData: formDataShape,
  modalOptions: modalOptionsShape,
  editingStatus: PropTypes.string,
};

const RELATIONSHIPS_MODAL = 'RELATIONSHIPS_MODAL';

export {
  getCurrentFields,
  RelationshipsModal,
  RELATIONSHIPS_MODAL,
};
